import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Shield, Star, Settings, Crown, Briefcase, FlaskConical, Heart, Check, X, Plus, Search, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface UserBadge {
  id: number;
  userId: number;
  badge: string;
  awardedBy: number | null;
  awardedAt: string;
  revokedAt: string | null;
  notes: string | null;
  syncedToStripe: boolean;
  userEmail: string;
  userFirstName: string | null;
  userLastName: string | null;
}

interface User {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  subscriptionStatus: string;
}

const BADGE_TYPES = [
  { value: 'official_bear_insider', label: 'Official Bear Insider', icon: Crown, color: 'bg-gradient-to-r from-yellow-400 to-orange-500' },
  { value: 'vip', label: 'VIP', icon: Star, color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  { value: 'early_adopter', label: 'Early Adopter', icon: FlaskConical, color: 'bg-gradient-to-r from-green-400 to-blue-500' },
  { value: 'moderator', label: 'Moderator', icon: Shield, color: 'bg-gradient-to-r from-blue-500 to-indigo-600' },
  { value: 'partner', label: 'Partner', icon: Briefcase, color: 'bg-gradient-to-r from-gray-600 to-gray-800' },
  { value: 'staff', label: 'Staff', icon: Settings, color: 'bg-gradient-to-r from-red-500 to-red-700' },
  { value: 'beta_tester', label: 'Beta Tester', icon: FlaskConical, color: 'bg-gradient-to-r from-teal-400 to-cyan-500' },
  { value: 'community_champion', label: 'Community Champion', icon: Heart, color: 'bg-gradient-to-r from-pink-500 to-rose-500' },
  { value: 'lifetime_member', label: 'Lifetime Member', icon: Crown, color: 'bg-gradient-to-r from-amber-500 to-yellow-600' },
];

const BadgeChip = ({ badge, size = "sm" }: { badge: string; size?: "sm" | "md" }) => {
  const badgeType = BADGE_TYPES.find(b => b.value === badge);
  if (!badgeType) return <Badge variant="secondary">{badge}</Badge>;
  
  const Icon = badgeType.icon;
  return (
    <Badge 
      className={`${badgeType.color} text-white border-0 ${size === 'md' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs'}`}
      data-testid={`badge-${badge}`}
    >
      <Icon className={`${size === 'md' ? 'w-4 h-4' : 'w-3 h-3'} mr-1`} />
      {badgeType.label}
    </Badge>
  );
};

export default function AdminBadges() {
  const [search, setSearch] = useState("");
  const [badgeFilter, setBadgeFilter] = useState("");
  const [showAwardDialog, setShowAwardDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedBadgeType, setSelectedBadgeType] = useState("");
  const [notes, setNotes] = useState("");
  const { toast } = useToast();

  // Fetch badges
  const { data: badgesResponse, isLoading, error, refetch } = useQuery<{
    badges: UserBadge[];
  }>({
    queryKey: ["/api/admin/badges"],
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  // Fetch users for badge awarding
  const { data: usersResponse } = useQuery<{
    users: User[];
  }>({
    queryKey: ["/api/admin/users?limit=100"],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const badges = badgesResponse?.badges || [];
  const users = usersResponse?.users || [];

  // Filter badges
  const filteredBadges = badges.filter(badge => {
    const matchesSearch = !search || 
      badge.userEmail.toLowerCase().includes(search.toLowerCase()) ||
      (badge.userFirstName && badge.userFirstName.toLowerCase().includes(search.toLowerCase())) ||
      (badge.userLastName && badge.userLastName.toLowerCase().includes(search.toLowerCase()));
    
    const matchesBadge = !badgeFilter || badgeFilter === "all" || badge.badge === badgeFilter;
    const isActive = !badge.revokedAt;
    
    return matchesSearch && matchesBadge && isActive;
  });

  // Award badge mutation
  const awardBadgeMutation = useMutation({
    mutationFn: async (data: { userId: number; badge: string; notes?: string }) => {
      return apiRequest("POST", "/api/admin/badges", data);
    },
    onSuccess: () => {
      toast({
        title: "Badge Awarded",
        description: "Badge has been successfully awarded to the user.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/badges"] });
      setShowAwardDialog(false);
      setSelectedUser(null);
      setSelectedBadgeType("");
      setNotes("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to award badge",
        variant: "destructive",
      });
    },
  });

  // Revoke badge mutation
  const revokeBadgeMutation = useMutation({
    mutationFn: async (badgeId: number) => {
      return apiRequest("DELETE", `/api/admin/badges/${badgeId}`);
    },
    onSuccess: () => {
      toast({
        title: "Badge Revoked",
        description: "Badge has been successfully revoked.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/badges"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to revoke badge",
        variant: "destructive",
      });
    },
  });

  const handleAwardBadge = () => {
    if (!selectedUser || !selectedBadgeType) return;
    
    awardBadgeMutation.mutate({
      userId: selectedUser.id,
      badge: selectedBadgeType,
      notes: notes || undefined,
    });
  };

  const handleRevokeBadge = (badgeId: number) => {
    if (confirm("Are you sure you want to revoke this badge?")) {
      revokeBadgeMutation.mutate(badgeId);
    }
  };

  const openAwardDialog = () => {
    setShowAwardDialog(true);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-2 mb-6">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading badges...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-2 text-red-600 mb-6">
          <AlertTriangle className="h-4 w-4" />
          <span>Error loading badges</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Badge Management</h2>
          <p className="text-gray-600">Manage user badges and achievements</p>
        </div>
        <Button onClick={openAwardDialog} data-testid="button-award-badge">
          <Plus className="h-4 w-4 mr-2" />
          Award Badge
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search Users</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Search by email or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-users"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="badge-filter">Badge Type</Label>
              <Select value={badgeFilter} onValueChange={setBadgeFilter}>
                <SelectTrigger data-testid="select-badge-filter">
                  <SelectValue placeholder="All badge types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All badge types</SelectItem>
                  {BADGE_TYPES.map((badgeType) => (
                    <SelectItem key={badgeType.value} value={badgeType.value}>
                      {badgeType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => refetch()}
                disabled={isLoading}
                data-testid="button-refresh"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Badge Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{badges.length}</div>
            <div className="text-sm text-gray-600">Total Badges</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{filteredBadges.length}</div>
            <div className="text-sm text-gray-600">Active Badges</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {badges.filter(b => b.syncedToStripe).length}
            </div>
            <div className="text-sm text-gray-600">Synced to Stripe</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {new Set(badges.filter(b => !b.revokedAt).map(b => b.userId)).size}
            </div>
            <div className="text-sm text-gray-600">Users with Badges</div>
          </CardContent>
        </Card>
      </div>

      {/* Badges List */}
      <Card>
        <CardHeader>
          <CardTitle>User Badges</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredBadges.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No badges found matching the current filters.
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBadges.map((badge) => (
                <div 
                  key={badge.id} 
                  className="flex items-center justify-between p-4 border rounded-lg"
                  data-testid={`badge-row-${badge.id}`}
                >
                  <div className="flex items-center space-x-4">
                    <BadgeChip badge={badge.badge} size="md" />
                    <div>
                      <div className="font-medium">
                        {badge.userFirstName && badge.userLastName 
                          ? `${badge.userFirstName} ${badge.userLastName}` 
                          : badge.userEmail}
                      </div>
                      <div className="text-sm text-gray-600">{badge.userEmail}</div>
                      {badge.notes && (
                        <div className="text-sm text-gray-500 mt-1">{badge.notes}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right text-sm">
                      <div className="text-gray-600">
                        Awarded {new Date(badge.awardedAt).toLocaleDateString()}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        {badge.syncedToStripe ? (
                          <Badge variant="secondary" className="text-xs">
                            <Check className="w-3 h-3 mr-1" />
                            Synced
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="text-xs">
                            <X className="w-3 h-3 mr-1" />
                            Not Synced
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRevokeBadge(badge.id)}
                      disabled={revokeBadgeMutation.isPending}
                      data-testid={`button-revoke-${badge.id}`}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Award Badge Dialog */}
      <Dialog open={showAwardDialog} onOpenChange={setShowAwardDialog}>
        <DialogContent data-testid="dialog-award-badge">
          <DialogHeader>
            <DialogTitle>Award Badge</DialogTitle>
            <DialogDescription>
              Select a user and badge type to award a new badge.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-select">User</Label>
              <Select 
                value={selectedUser?.id.toString() || ""} 
                onValueChange={(value) => {
                  const user = users.find(u => u.id === parseInt(value));
                  setSelectedUser(user || null);
                }}
              >
                <SelectTrigger data-testid="select-user">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.firstName && user.lastName 
                        ? `${user.firstName} ${user.lastName} (${user.email})` 
                        : user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="badge-select">Badge Type</Label>
              <Select value={selectedBadgeType} onValueChange={setSelectedBadgeType}>
                <SelectTrigger data-testid="select-badge-type">
                  <SelectValue placeholder="Select a badge type" />
                </SelectTrigger>
                <SelectContent>
                  {BADGE_TYPES.map((badgeType) => (
                    <SelectItem key={badgeType.value} value={badgeType.value}>
                      <div className="flex items-center space-x-2">
                        <badgeType.icon className="w-4 h-4" />
                        <span>{badgeType.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add notes about this badge award..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="textarea-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAwardDialog(false)}
              data-testid="button-cancel-award"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAwardBadge}
              disabled={!selectedUser || !selectedBadgeType || awardBadgeMutation.isPending}
              data-testid="button-confirm-award"
            >
              {awardBadgeMutation.isPending ? "Awarding..." : "Award Badge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}